import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { BusinessRole } from '../models/businessRole';
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Typography,
  Button,
  Divider,
  Menu,
  MenuItem,
  Alert,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  CalendarMonth as CalendarMonthIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';
import { offlineService } from '../services/offlineService';
import OnboardingGuide from './onboarding/OnboardingGuide';
import { useSignalRSync } from '../hooks/useSignalRSync';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isOffline, setIsOffline] = useState(!offlineService.getOnlineStatus());
  const { isAuthenticated, logout, user, hasBusinessSetup, isOfflineAuth, needsReauth } = useAuth();
  const { theme, setBusinessId } = useBusinessTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isCalendarView = location.pathname === '/calendar';
  const isBusinessSetupPage = location.pathname === '/business-setup';

  // Sync SignalR events with TanStack Query cache
  useSignalRSync(user?.businessId || undefined);

  useEffect(() => {
    if (user?.businessId) {
      setBusinessId(user.businessId);
    } else {
      setBusinessId(null);
    }
    // Debug logging
    console.log('User:', user);
    console.log('Business Roles:', user?.businessRoles);
  }, [user, setBusinessId]);

  // Offline status monitoring
  useEffect(() => {
    const handleConnectivityChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('[Layout] Connectivity changed:', customEvent.detail.isOnline);
      setIsOffline(!customEvent.detail.isOnline);
    };

    // Listen for offline/online events
    window.addEventListener('connectivity-changed', handleConnectivityChange);
    
    // Also check offline status periodically
    const checkOfflineStatus = () => {
      const currentOfflineStatus = !offlineService.getOnlineStatus();
      console.log('[Layout] Periodic offline check:', currentOfflineStatus);
      setIsOffline(currentOfflineStatus);
    };
    
    // Check immediately and then every 5 seconds
    checkOfflineStatus();
    const interval = setInterval(checkOfflineStatus, 5000);
    
    return () => {
      window.removeEventListener('connectivity-changed', handleConnectivityChange);
      clearInterval(interval);
    };
  }, []);

  const hasBusinessAccess = user?.isAdmin || user?.businessRoles?.some((role) => {
    console.log('Checking role:', role);
    return role.isBusinessAdmin === true || role.role === 'Admin';
  });

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuNavigation = (path: string) => {
    handleClose();
    navigate(path);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: theme?.navigationColor || '#1976d2',
          color: '#fff'
        }}
      >
        <Toolbar>
          <CalendarMonthIcon sx={{ mr: 1, color: theme.secondaryColor }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" noWrap component={Link} to="/dashboard" sx={{ textDecoration: 'none', color: theme.secondaryColor }}>
              MindShare
            </Typography>
          </Box>
          
          {isAuthenticated ? (
            <>
              {hasBusinessSetup && (
                <>
                  <IconButton
                    color="inherit"
                    component={Link}
                    to="/calendar"
                    sx={{ 
                      mr: 1,
                      ...(location.pathname === '/calendar' && {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '4px',
                      }),
                    }}
                    aria-label="calendar"
                  >
                    <CalendarMonthIcon />
                  </IconButton>
                  
                  <IconButton
                    color="inherit"
                    component={Link}
                    to="/crm/customers"
                    sx={{
                      mr: 1,
                      ...(location.pathname.startsWith('/crm') && {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '4px',
                      }),
                    }}
                    aria-label="customers"
                  >
                    <AssignmentIcon />
                  </IconButton>
                </>
              )}
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <AccountCircleIcon />
              </IconButton>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                color="inherit" 
                component={Link} 
                to="/login"
              >
                Login
              </Button>
              <Button 
                color="inherit" 
                component={Link} 
                to="/register"
                variant="outlined"
                sx={{ borderColor: 'white' }}
              >
                Register
              </Button>
            </Box>
          )}
          
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem 
              onClick={() => handleMenuNavigation('/dashboard')}
              sx={{
                ...(location.pathname === '/dashboard' && {
                  fontWeight: 'bold',
                  backgroundColor: (theme) => theme.palette.action.selected,
                }),
              }}
            >
              Dashboard
            </MenuItem>
            <MenuItem 
              onClick={() => handleMenuNavigation('/account')}
              sx={{
                ...(location.pathname === '/account' && {
                  fontWeight: 'bold',
                  backgroundColor: (theme) => theme.palette.action.selected,
                }),
              }}
            >
              My Account
            </MenuItem>
            {hasBusinessAccess && (
              <MenuItem
                onClick={() => handleMenuNavigation('/business-settings')}
                sx={{
                  ...(location.pathname === '/business-settings' && {
                    fontWeight: 'bold',
                    backgroundColor: (theme) => theme.palette.action.selected,
                  }),
                }}
              >
                Business Settings
              </MenuItem>
            )}
            {user?.isAdmin && (
              <MenuItem
                onClick={() => handleMenuNavigation('/admin/form-editor')}
                sx={{
                  ...(location.pathname === '/admin/form-editor' && {
                    fontWeight: 'bold',
                    backgroundColor: (theme) => theme.palette.action.selected,
                  }),
                }}
              >
                Form Editor
              </MenuItem>
            )}
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>

        </Toolbar>
      </AppBar>
      
      <Toolbar />
      
      {/* Offline Banner - Hide on calendar view */}
      {(() => {
        console.log('[Layout] Banner conditions:', { isAuthenticated, isOffline, isCalendarView, shouldShow: isAuthenticated && isOffline && !isCalendarView });
        return isAuthenticated && isOffline && !isCalendarView;
      })() && (
        <Alert 
          severity="warning" 
          sx={{ 
            borderRadius: 0,
            '& .MuiAlert-message': {
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }
          }}
        >
          Working offline with local data. Some features may be limited.
        </Alert>
      )}
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isAuthenticated ? (isCalendarView ? 0 : { xs: 1, sm: 2 }) : { xs: 2, sm: 3 },
          width: '100%',
          backgroundColor: theme?.backgroundColor || '#f5f5f4',
          color: theme?.textColor || '#333',
          height: { xs: 'calc(100vh - 64px)', sm: 'calc(100vh - 88px)' },
        }}
      >
        {children}
      </Box>
      
      {/* Hide onboarding guide on business setup page */}
      {!isBusinessSetupPage && (
        <OnboardingGuide 
          primaryColor={theme?.navigationColor || '#2196f3'} 
          backgroundColor={theme?.subtleAccent || '#f5f7fa'}
          textColor={theme?.textColor || '#333'}
        />
      )}

    </Box>
  );
};

export default Layout; 